// 3rd Party
import React, { useEffect, useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { connect } from 'react-redux';

// Components
import UploadStep from './UploadStep';
import ModalV2 from '../ModalV2';
import ProcessingFile from '../../common/ProcessingFile';
import { FormattedMessageV2 } from '../../i18n';

// Utils
import { formatMessage, LOCALE } from '@/utils/i18n';
import { MODAL_SIZES } from '@/utils/constants';
import notification from '@/utils/notification';

// Types
import Contact from '@/types/contact';
import Dictionary from '@/types/dictionary';
import PortfolioHolding from '@/types/portfolio-holding';

// Redux
import {
  processPortfolioFiles,
  PROCESS_PORTFOLIO,
  convertProcessedStatementToPortfolio,
  cancelAIStatementReaderProcessing,
  setReadEndTime,
  setReadStartTime,
} from '@/redux/modules/ai-statement-reader';
import { selectIsLoadingEvery, startLoaderAction, stopLoaderAction } from '@/redux/modules/ui/loaders';
import { ReduxState } from '@/redux/modules/types';
import { loadNewPortfolio } from '@/redux/modules/comparison';
import { togglePortfolioEditor } from '@/redux/modules/common/actionCreators';
import { ALLOWED_HTML_TAGS } from '../../i18n/FormattedMessageV2';

type DispatchProps = {
  processPortfolioFiles: (files: File[]) => Promise<void>;
  convertProcessedStatementToPortfolio: (processedFiles: PortfolioHolding[]) => void;
  cancelAIStatementReaderProcessing: (files: File[], isProcessing?: boolean, isProcessed?: boolean) => void;
  setReadEndTime: () => void;
  setReadStartTime: () => void;
  startLoaderAction: (name: string, params?: Dictionary<unknown>) => void;
  stopLoaderAction: (name: string) => void;
};

type StateProps = {
  isProcessingPortfolio: boolean;
  processedFiles: Array<PortfolioHolding>;
  processingError: boolean;
  currentContact?: Contact;
};

export type Props = DispatchProps &
  StateProps & {
    onCancel: () => void;
    show: boolean;
    locale: string;
  };

export enum AIStatementReaderSteps {
  UPLOAD,
  PROCESSING,
}

function PortfolioAiStatementReaderModal({
  show,
  onCancel,
  locale,
  currentContact,
  processedFiles,
  processingError,
  processPortfolioFiles,
  convertProcessedStatementToPortfolio,
  cancelAIStatementReaderProcessing,
  isProcessingPortfolio,
  setReadEndTime,
  setReadStartTime,
}: Props) {
  const i18nBasePath = 'Modal.PortfolioAIStatementReader';
  const localePath = locale === LOCALE.FR_CA ? 'fr' : 'en';

  const [currentStep, setCurrentStep] = useState<AIStatementReaderSteps>(AIStatementReaderSteps.UPLOAD);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [fileRejections, setFileRejections] = useState<FileRejection[]>([]);

  // useEffect for processedFiles
  useEffect(() => {
    if (processedFiles?.length) {
      setReadEndTime();
      convertProcessedStatementToPortfolio(processedFiles);
      finishProgressAndCloseFlow();
      togglePortfolioEditor(true);
      notification.success(formatMessage(`${i18nBasePath}.SuccessfullyUploadedToast`));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedFiles]);

  // Original useEffect for processingError
  useEffect(() => {
    if (processingError) {
      setCurrentStep(AIStatementReaderSteps.UPLOAD);
    }
  }, [processingError]);

  const closeFlow = (isProcessed?: boolean) => {
    cancelAIStatementReaderProcessing(uploadedFiles, isProcessingPortfolio, isProcessed);
    setUploadedFiles([]);
    setFileRejections([]);
    setCurrentStep(AIStatementReaderSteps.UPLOAD);
    onCancel();
  };

  const finishProgressAndCloseFlow = () => {
    setTimeout(() => closeFlow(true), 300);
  };

  const processFiles = () => {
    setReadStartTime();
    setCurrentStep(AIStatementReaderSteps.PROCESSING);
    loadNewPortfolio(currentContact);
    processPortfolioFiles(uploadedFiles);
  };

  const renderBody = () => {
    switch (currentStep) {
      case AIStatementReaderSteps.UPLOAD:
        return (
          <UploadStep
            onDrop={(newUploadedFiles, newFileRejections) => {
              setUploadedFiles([...uploadedFiles, ...newUploadedFiles]);
              setFileRejections([...fileRejections, ...newFileRejections]);
            }}
            onRemove={(file: File) => {
              setUploadedFiles(uploadedFiles.filter(uploadedFile => uploadedFile !== file));
              setFileRejections(fileRejections.filter(fileRejection => fileRejection.file !== file));
              cancelAIStatementReaderProcessing(uploadedFiles.filter(uF => uF === file));
            }}
            uploadedFiles={uploadedFiles}
            fileRejections={fileRejections}
            processingError={processingError}
          />
        );
      case AIStatementReaderSteps.PROCESSING:
        return (
          <div className="processing-container">
            <ProcessingFile isProcessing={isProcessingPortfolio} fileSize={uploadedFiles.reduce((acc, file) => acc + file.size, 0)} />
          </div>
        );
      default:
        return null;
    }
  };

  const renderFooterButtons = () => {
    switch (currentStep) {
      case AIStatementReaderSteps.UPLOAD:
        return {
          cancelLabelToLocalize: 'Common.Cancel',
          cancelOnClick: () => closeFlow(),
          primaryLabelToLocalize: 'Common.Continue',
          primaryOnClick: () => processFiles(),
          primaryDisabled: !uploadedFiles.length || !!fileRejections.length,
        };
      case AIStatementReaderSteps.PROCESSING:
        return {};
      default:
        return {};
    }
  };

  const renderSupport = () => (
    <div className="support-container">
      <FormattedMessageV2 tagName={ALLOWED_HTML_TAGS.SPAN} toLocalize={`${i18nBasePath}.Support.ByClickingContinue`} />
      <a className="support-link" href={`https://capintel.com/${localePath}/terms`} target="_blank" rel="noopener noreferrer">
        <FormattedMessageV2 tagName={ALLOWED_HTML_TAGS.SPAN} toLocalize={`${i18nBasePath}.Support.Terms`} />
      </a>
      <FormattedMessageV2 tagName={ALLOWED_HTML_TAGS.SPAN} toLocalize={`${i18nBasePath}.Support.And`} />
      <a className="support-link" href={`https://capintel.com/${localePath}/privacy`} target="_blank" rel="noopener noreferrer">
        <FormattedMessageV2 tagName={ALLOWED_HTML_TAGS.SPAN} toLocalize={`${i18nBasePath}.Support.PrivacyPolicy`} />
      </a>
    </div>
  );

  return (
    <ModalV2
      show={show}
      title={`${i18nBasePath}.Title`}
      modalClassName="portfolio-ai-statement-reader-modal"
      body={renderBody()}
      onClose={() => closeFlow()}
      size={MODAL_SIZES.LG}
      defaultFooterButtons={renderFooterButtons()}
      support={renderSupport()}
    />
  );
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  isProcessingPortfolio: selectIsLoadingEvery(state, [PROCESS_PORTFOLIO.type]),
  processedFiles: state.aiStatementReader.current.processedFiles,
  processingError: state.aiStatementReader.current.processingError,
  currentContact: state.contacts.current?.contact,
});

export default connect(mapStateToProps, {
  processPortfolioFiles,
  convertProcessedStatementToPortfolio,
  cancelAIStatementReaderProcessing,
  setReadEndTime,
  setReadStartTime,
  startLoaderAction,
  stopLoaderAction,
})(PortfolioAiStatementReaderModal);
